import React, { useEffect, useContext, useRef, useState } from 'react';
import tw from 'twin.macro';
import { Icon } from '../icon';
import { VideoIcon, ImageIcon, CheckIcon, DropIcon } from 'icons';
import { formatDuration } from '../../lib/helpers';
import { PreferenceContext } from '../context';

const ToppersCorner = ({ number, page }) => (
  <div tw='absolute w-16 flex justify-center items-center top-0.5 pt-0.5 -left-3 z-10 rounded-tl-lg'>
    <div tw='-ml-3 w-8 font-black text-center text-dark text-xs z-10'>
      <small>{page ? number + page * 24 + 1 : number + 1}</small>
    </div>
    <DropIcon tw='text-brand absolute -ml-1 -top-0.5 drop-shadow-md rounded-tl-lg' />
  </div>
);

export const CreateItemImage = ({
  isNsfw,
  thumbImage,
  landscape,
  useCF,
  notRounded,
  ...restProps
}) => {
  if (useCF) {
    thumbImage = `https://dumpert.nl/cdn-cgi/image/format=auto/${thumbImage}`;
  }

  const {
    preference: { nsfwActive },
  } = useContext(PreferenceContext);
  const canvasRef = useRef();
  const { current } = canvasRef;
  const [renderImage, setRenderImage] = useState(false);

  useEffect(() => {
    setRenderImage(true);
    if (current) {
      const img = new Image();
      img.onload = () => {
        const { width, height } = img;
        const pixelValue = landscape ? 50 : 15;
        const multiply = landscape ? 1.5 : 2;

        current.width = width * multiply;
        current.height = height * multiply;
        const ctx = current.getContext('2d');

        const fw = (width / pixelValue) | 0;
        const fh = (height / pixelValue) | 0;

        ctx.imageSmoothingEnabled = false;
        ctx.mozImageSmoothingEnabled = false;
        ctx.msImageSmoothingEnabled = false;
        ctx.webkitImageSmoothingEnabled = false;

        ctx.drawImage(img, 0, 0, fw * multiply, fh * multiply);
        ctx.drawImage(
          current,
          0,
          0,
          fw * multiply,
          fh * multiply,
          0,
          0,
          width * multiply,
          height * multiply,
        );
      };
      img.src = thumbImage || '';
      setRenderImage(false);
    }
  }, [current, nsfwActive, landscape, renderImage]);

  return (
    <>
      {isNsfw ? (
        <canvas ref={canvasRef} {...restProps} />
      ) : (
        <img
          css={[
            tw`w-full h-full object-cover object-center rounded-lg transition-transform duration-300 transform-gpu`,
            !landscape && tw`md:group-[.item-transition]:hover:scale-125`,
            landscape && tw`md:group-[.item-transition]:hover:scale-[1.10]`,
            notRounded && tw`rounded-none`,
          ]}
          src={thumbImage}
          loading='lazy'
          {...restProps}
          alt=''
        />
      )}
    </>
  );
};

export const ItemImage = ({
  item,
  index,
  isTopper,
  hideSummary = false,
  landscape = false,
  useCF = false,
  notRounded,
  period,
  page,
  isSidebar,
  isPlaylistItem,
  ...restProps
}) => {
  const {
    preference: { highlightedItems },
  } = useContext(PreferenceContext);
  const [highlightMedia, setHighlightMedia] = useState(false);

  const kudoNum = isTopper && isSidebar ? item.stats.kudos_today : item.stats.kudos_total;
  const allImages = item.media.filter((i) => i.mediatype === 'FOTO');
  const allVideos = item.media.filter((i) => i.mediatype === 'VIDEO');
  const webpOrientation = landscape ? 'still-med-webp' : 'thumb-med-webp';
  const hasWebpImage = item.media?.[0]?.variants?.some((obj) => obj.version === webpOrientation);
  const thumbImage = hasWebpImage
    ? item.media[0].variants.find((obj) => obj.version === webpOrientation).uri
    : item.stills[!landscape || item.nsfw ? 'thumb' : 'still-medium'] || '';

  useEffect(() => {
    try {
      setHighlightMedia(
        (window.sessionStorage.getItem('viewedItems') || '').split(',').includes(item.id),
      );
    } catch (e) {
      // no-op
    }
  });

  return (
    <>
      {isTopper && <ToppersCorner number={index} page={!period && page} />}
      <div
        css={[
          tw`rounded-lg bg-icons relative overflow-hidden`,
          notRounded && tw`rounded-none`,
          !landscape && tw`w-28 h-28 flex-shrink-0`,
          landscape && tw`aspect-w-16 aspect-h-9 relative`,
          isPlaylistItem && tw`aspect-w-11 aspect-h-1 relative w-14 h-14`,
        ]}
        {...restProps}
      >
        <CreateItemImage
          isNsfw={item.nsfw}
          thumbImage={thumbImage}
          landscape={landscape}
          useCF={useCF}
          notRounded={notRounded}
        />
        {highlightMedia && highlightedItems && (
          <div tw='absolute top-0 w-full h-full flex justify-center rounded-lg items-center bg-dark opacity-70 backdrop-brightness-50'>
            <Icon Type={CheckIcon} color='success' size='large' />
          </div>
        )}
        {!hideSummary && (
          <div
            css={[
              tw`absolute bottom-0 top-auto! w-full !h-auto bg-dark/[.85] px-1.5 py-1.5
      rounded-b-lg flex justify-between items-center z-10! text-xs text-light`,
              landscape && tw`px-3`,
            ]}
          >
            <div tw='flex items-center gap-1'>
              {allVideos.length > 0 && (
                <>
                  <Icon Type={VideoIcon} size={landscape ? 'medium' : 'small'} color='icons' />
                  <span> {formatDuration(allVideos[0].duration, allVideos.length, 'VIDEO')}</span>
                </>
              )}
              {allImages.length > 0 && (
                <>
                  <Icon Type={ImageIcon} size={landscape ? 'medium' : 'small'} color='icons' />
                  <span> {formatDuration(0, allImages.length, 'FOTO')}</span>
                </>
              )}
            </div>
            <div
              css={[
                tw`flex items-center font-bold`,
                kudoNum < 0 && tw`text-danger`,
                kudoNum > 0 && tw`text-success`,
              ]}
            >
              <span>
                {kudoNum > 0 ? '+' : null}
                {kudoNum}
              </span>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
